import type { JSX } from 'react';
import { Image } from 'antd';
import { FileOutlined, FilePdfOutlined, LayoutOutlined } from '@ant-design/icons';

import type { ThreadFileDto } from '../../lib/api/openingTasks';
import { fileExt, fmtDuration, fmtSize } from './utils';

/**
 * Вложения переписки: фото — миниатюры с просмотром на весь экран (antd
 * Image.PreviewGroup: листание, зум, поворот), видео — плеер браузера,
 * планировки и файлы — чипы со скачиванием. URL файлов приходят готовыми
 * (`/api/wiki/opening/thread-files/<id>` → редирект в хранилище).
 */

/** Просмотр фото на весь экран; index === null — закрыт. */
export function PhotoLightbox({
  urls,
  index,
  onChange,
  onClose,
}: {
  urls: string[];
  index: number | null;
  onChange: (i: number) => void;
  onClose: () => void;
}): JSX.Element | null {
  if (index == null || urls.length === 0) return null;
  return (
    <Image.PreviewGroup
      items={urls}
      preview={{
        open: true,
        current: Math.min(index, urls.length - 1),
        onChange: (cur) => onChange(cur),
        onOpenChange: (open) => {
          if (!open) onClose();
        },
      }}
    />
  );
}

export function PhotoThumb({ file, onOpen }: { file: ThreadFileDto; onOpen: () => void }): JSX.Element {
  return (
    <button type="button" className="fk-ot-ph" onClick={onOpen} title={file.filename} aria-label={`Открыть фото ${file.filename}`}>
      <img src={file.url} alt={file.filename} loading="lazy" decoding="async" />
    </button>
  );
}

export function VideoBox({ file }: { file: ThreadFileDto }): JSX.Element {
  const dur = fmtDuration(file.durationSec);
  return (
    <div className="fk-ot-video">
      <video controls preload="metadata" playsInline src={file.url} />
      <small>
        {file.filename}
        {dur ? ` · ${dur}` : ''}
        {file.sizeBytes ? ` · ${fmtSize(file.sizeBytes)}` : ''}
      </small>
    </div>
  );
}

export function FileChip({ file }: { file: ThreadFileDto }): JSX.Element {
  const ext = fileExt(file.filename);
  const icon = ext === 'pdf' ? <FilePdfOutlined /> : file.kind === 'plan' ? <LayoutOutlined /> : <FileOutlined />;
  return (
    <a className="fk-ot-file" href={file.url} target="_blank" rel="noopener noreferrer" download={file.filename} title={`Скачать ${file.filename}`}>
      {icon}
      <span>{file.filename}</span>
      {file.sizeBytes ? <small>{fmtSize(file.sizeBytes)}</small> : null}
    </a>
  );
}

/** Вложения одного сообщения: ряд миниатюр фото, видео, чипы файлов. */
export function MessageFiles({ files, onOpenPhoto }: { files: ThreadFileDto[]; onOpenPhoto: (fileId: string) => void }): JSX.Element | null {
  if (!files.length) return null;
  const photos = files.filter((f) => f.kind === 'photo');
  const videos = files.filter((f) => f.kind === 'video');
  const docs = files.filter((f) => f.kind !== 'photo' && f.kind !== 'video');
  return (
    <>
      {photos.length ? (
        <div className="fk-ot-atts">
          {photos.map((f) => (
            <PhotoThumb key={f.id} file={f} onOpen={() => onOpenPhoto(f.id)} />
          ))}
        </div>
      ) : null}
      {videos.map((f) => (
        <VideoBox key={f.id} file={f} />
      ))}
      {docs.length ? (
        <div className="fk-ot-atts">
          {docs.map((f) => (
            <FileChip key={f.id} file={f} />
          ))}
        </div>
      ) : null}
    </>
  );
}
